<template>
  <div id="pending-users">
    <v-container fluid>
      <v-flex xs12>
        <v-alert
          v-if="feedback"
          border="start"
          color="#592892"
          class="text-center"
        >
          {{ feedback }}
        </v-alert>
        <HowToUseThisPage />
        <div class="users-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <TwoPeople :color="iconColor" class="users-icon" />
              {{ $t('pendingUsers.title') }}
            </h1>
          </div>
          <v-card v-if="pendingusers" color="transparent" flat>
            <v-card-title
              class="px-0 pb-6 d-flex align-center flex-wrap flex-sm-nowrap"
            >
              <span class="users-action-bar font-weight-bold"
                >{{ $t('pendingUsers.totalPendingUsers') }}:
                {{ filteredUsers.length }}</span
              >
              <v-spacer></v-spacer>
              <v-select
                v-model="selectedMonth"
                :items="translatedMonths"
                :label="$t('pendingUsers.filterByMonth')"
                density="compact"
                variant="solo"
                clearable
                flat
                hide-details
                color="#A5D867"
                class="mr-4"
                @update:model-value="filterByMonth(selectedMonth)"
              ></v-select>
              <v-text-field
                v-model="search"
                :append-inner-icon="search ? '' : 'mdi-magnify'"
                :label="$t('pendingUsers.searchPlaceholder')"
                single-line
                hide-details
                clearable
                variant="solo"
                flat
                density="compact"
                color="#E8E9EC"
              ></v-text-field>
            </v-card-title>
          </v-card>
          <v-data-table
            :headers="adminActions"
            fixed-header
            :loading="isloading"
            :loading-text="$t('pendingUsers.loadingUsers')"
            :no-data-text="$t('pendingUsers.noUsersForChurch')"
            :items="filteredUsers"
            :items-per-page="20"
            :header-props="{ sortIcon: '$sortarrowdown' }"
            :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
            v-model:expanded="expanded"
            expand-icon="$edit"
            item-key="email"
            :single-expand="singleExpand"
            :search="search"
            class="elevation-1 users-table"
          >
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>
            <template #[`item.url`]="data">
              <div>
                <v-avatar v-if="data.item.url" start size="27">
                  <v-img :src="data.item.url" />
                </v-avatar>
                <v-avatar v-else color="#cfebae" start size="27">
                  <span>{{ shortHand(data.item.user_name) }}</span>
                </v-avatar>
              </div>
            </template>

            <template #[`item.email`]="{ item }">
              <div
                v-if="item.email"
                class="users-email"
                :class="{ active: showedit === item.id }"
              >
                <div>{{ item.email || '—' }}</div>
              </div>
            </template>
            <template #[`item.group`]="{ item }">
              <div :class="item.groupID || ''">
                {{ item.groupID ? lookUpGroup(item.groupID) : '—' }}
              </div>
            </template>

            <template #[`item.sent`]="{ item }">
              <div>{{ item.sent ? formatDate(item.sent) : '—' }}</div>
            </template>

            <template
              v-if="
                isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
              "
              #[`item.actions`]="{ item }"
            >
              <div class="action-icons">
                <v-icon
                  @click.native="reinviteMember(item)"
                  class="email-icon mr-6"
                  >email</v-icon
                >
                <v-icon
                  v-if="!isHelpDesk()"
                  @click.native="deleteItem(item)"
                  >delete_icon</v-icon
                >
              </div>
            </template>
          </v-data-table>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  // import firebase from 'firebase'
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { formatDate } from '@/components/utilities/formatters'
  import { mapGetters } from 'vuex'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import TwoPeople from '@/assets/icons/TwoPeople.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Users',
    data() {
      return {
        feedback: null,
        pendingusers: [],
        groups: [],
        expanded: [],
        singleExpand: true,
        isloading: true,
        isFormValid: false,
        isediting: false,
        showedit: false,
        search: '',
        activeUser: false,
        // headers: [
        //     { text: 'Avatar', sortable: false, filterable: false, value: 'url', },
        //     { text: 'Name', value: 'user_name' },
        //     { text: 'Email', sortable: false, value: 'email' },
        //     { text: 'Group', filterable: false, value: 'group' },
        //     { text: 'Access', filterable: false, value: 'access' },
        //     { text: 'Active', filterable: false, value: 'active' },
        //     { text: 'Actions', sortable: false, filterable: false, value: 'actions' },
        // ],
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        usersRoles: ['admin', 'member'],
        possibleLeaderOptions: [
          {
            text: 'yes',
            value: true,
          },
          {
            text: 'no',
            value: false,
          },
        ],
        dateFormatConfig: {
          dayOfWeekNames: [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ],
          dayOfWeekNamesShort: ['Su', 'Mo', 'Tu', 'We', 'Tr', 'Fr', 'Sa'],
          monthNames: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ],
          monthNamesShort: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ],
          // Timezone offset, in minutes (0 - UTC, 180 - Russia, undefined - current)
          timezone: 0,
        },
        selectedMonth: null,
        months: [],
        filtered: '',
        areLeadersVisible: false,
      }
    },
    components: {
      HowToUseThisPage,
      TwoPeople,
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      shortHand() {
        return (name) => {
          if (name) {
            let words = name.split(' ')
            let short_hand = words[0][0] + words[words.length - 1][0]
            return short_hand // <-- The return value as Per logic
          }
          return 'NN'
        }
      },
      adminActions() {
        let h = [
          {
            title: this.$t('pendingUsers.tableHeaders.email'),
            sortable: true,
            key: 'email',
          },
          {
            title: this.$t('pendingUsers.tableHeaders.group'),
            sortable: false,
            key: 'group',
          },
          {
            title: this.$t('pendingUsers.tableHeaders.inviteSent'),
            sortable: true,
            filterable: false,
            key: 'sent',
          },
          {
            title: this.$t('pendingUsers.tableHeaders.actions'),
            sortable: false,
            filterable: false,
            key: 'actions',
            align: 'end',
          },
        ]
        if (this.isAuthenticated) {
          if (this.checkAuthorization(['admin']))
            h = [
              {
                title: this.$t('pendingUsers.tableHeaders.email'),
                sortable: true,
                key: 'email',
              },
              {
                title: this.$t('pendingUsers.tableHeaders.group'),
                sortable: false,
                key: 'group',
              },
              {
                title: this.$t('pendingUsers.tableHeaders.inviteSent'),
                sortable: true,
                filterable: false,
                key: 'sent',
              },
              {
                title: this.$t('pendingUsers.tableHeaders.actions'),
                sortable: false,
                filterable: false,
                key: 'actions',
              },
            ]
        }
        return h
      },
      translatedMonths() {
        return [
          this.$t('pendingUsers.months.january'),
          this.$t('pendingUsers.months.february'),
          this.$t('pendingUsers.months.march'),
          this.$t('pendingUsers.months.april'),
          this.$t('pendingUsers.months.may'),
          this.$t('pendingUsers.months.june'),
          this.$t('pendingUsers.months.july'),
          this.$t('pendingUsers.months.august'),
          this.$t('pendingUsers.months.september'),
          this.$t('pendingUsers.months.october'),
          this.$t('pendingUsers.months.november'),
          this.$t('pendingUsers.months.december'),
        ]
      },
      filteredUsers() {
        if (!this.filtered) return this.pendingusers
        return this.pendingusers.filter((item) => {
          return formatDate(item.sent).slice(0, 3) === this.filtered.slice(0, 3)
        })
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          this.init(event)
        },
      },
    },
    methods: {
      formatDate,
      activateUser(user) {
        db.collection('pendingusers')
          .doc(user.id)
          .update({
            active: user.active,
            activated: Date.now(),
          })
          .catch((err) => {
            console.log(err)
          })
      },
      customFilter(items, search, filter) {
        console.log(items)
        search = search.toString().toLowerCase()
        return items.filter((row) => filter(row['group'], search))
        // return Object.values(items).some(v=>v&&v.toString().toLowerCase().includes(search))
      },
      editUser(user) {
        this.isediting = true
        // console.log('editUser ', user)
        // Set a timeout for the user experience
        setTimeout(() => {
          db.collection('pendingusers')
            .doc(user.id)
            .update({
              user_name: user.user_name,
              // group: user.group,
              roles: user.roles,
              possibleLeader: user.possibleLeader,
            })
            .then(() => {
              // console.log('complete')
              this.isediting = false
            })
        }, 1000)
      },
      async deleteItem(item) {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'pendingUsers.messages.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete pending users.'
          return
        }

        if (!item) {
          Swal.fire({
            title: this.$t('pendingUsers.messages.errorTitle'),
            text: this.$t('pendingUsers.messages.errorDelete'),
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const confirmed = await confirmAction({
          title: `${this.$t('pendingUsers.dialogs.delete.title')} <br>"${
            item.email
          }"`,
          html: this.$t('pendingUsers.dialogs.delete.html'),
          input: 'text',
          inputPlaceholder: this.$t(
            'pendingUsers.dialogs.delete.inputPlaceholder'
          ),
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'DELETE')) {
                resolve()
              } else {
                resolve(this.$t('pendingUsers.dialogs.delete.inputValidator'))
              }
            })
          },
          confirmButtonText: this.$t(
            'pendingUsers.dialogs.delete.confirmButton'
          ),
          cancelButtonText: this.$t('pendingUsers.dialogs.delete.cancelButton'),
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        try {
          await db.collection('pendingusers').doc(item.id).delete()
          this.feedback = `${this.$t('pendingUsers.messages.userRemoved')} ${
            item.email
          }`
          this.init(this.$store.getters['AppData/cid'])
        } catch (err) {
          console.error('Error deleting user:', err)
        }
      },
      async reinviteMember(item) {
        this.feedback = ''

        if (!item) {
          Swal.fire({
            title: this.$t('pendingUsers.messages.errorTitle'),
            text: this.$t('pendingUsers.messages.errorInvite'),
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const confirmed = await confirmAction({
          title: `${this.$t('pendingUsers.dialogs.reinvite.title')} <br>"${
            item.email
          }"`,
          html: this.$t('pendingUsers.dialogs.reinvite.html'),
          input: 'text',
          inputPlaceholder: this.$t(
            'pendingUsers.dialogs.reinvite.inputPlaceholder'
          ),
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'INVITE')) {
                resolve()
              } else {
                resolve(this.$t('pendingUsers.dialogs.reinvite.inputValidator'))
              }
            })
          },
          confirmButtonText: this.$t(
            'pendingUsers.dialogs.reinvite.confirmButton'
          ),
          cancelButtonText: this.$t(
            'pendingUsers.dialogs.reinvite.cancelButton'
          ),
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        try {
          await db.collection('pendingusers').doc(item.id).delete()

          await this.$store.dispatch('UserData/inviteUserWithEmailLink', {
            email: item.email,
            cid: this.$store.getters['AppData/cid'],
            groupID: item.groupID,
          })
        } catch (err) {
          console.error('Error resending invite:', err)
        }
      },
      userFilters(which) {
        if (which == 'show-leaders') {
          this.areLeadersVisible = !this.areLeadersVisible
        }
        // if(which == 'clear') {
        //     this.getCourses(this.$store.getters['AppData/cid'])
        // }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      filterByMonth(month) {
        this.filtered = month
      },
      lookUpGroup(groupID) {
        const inGroup = this.groups.filter(
          (item) => item.group_doc_id === groupID
        )
        if (inGroup.length > 0) return inGroup[0].name
      },
      checkForLeader(item) {
        // console.log('inGroup ',item)
        if (item.roles.includes('leader')) return true
      },
      init(cid) {
        this.pendingusers = []
        this.isloading = true

        // we set a timeout for the User experience
        setTimeout(() => {
          let usersRef = db.collection('pendingusers')
          usersRef = usersRef.where('cid', '==', cid)
          usersRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let user = doc.data()
                user.id = doc.id
                this.pendingusers.push(user)
              })
            })
            .then(() => {
              let groupsRef = db.collection('groups')
              groupsRef = groupsRef.where('cid', '==', cid)
              groupsRef
                .get()
                .then((snapshot) => {
                  snapshot.forEach((doc) => {
                    let group = doc.data()
                    group.group_doc_id = doc.id
                    this.groups.push(group)
                  })
                })
                .then(() => {
                  this.isloading = false
                })
            })
        }, 1000)
      },
    },
    created() {
      // this.allUsers()
      this.init(this.$store.getters['AppData/cid'])

      this.$store.watch(
        () => {
          return this.$store.getters['UserData/getLoginFeedback'] // could also put a Getter here
        },
        (newValue) => {
          // console.log('newValue', newValue, 'oldValue', oldValue)
          //something changed do something
          if (newValue) {
            if (
              newValue.code &&
              (newValue.code == 'resubmit-verification' ||
                newValue.code == 'no-verfied email')
            ) {
              this.verifyEmail = true
            }
            this.feedback = newValue.message
            this.loading = false
          }
        },
        //Optional Deep if you need it
        {
          deep: true,
        }
      )
    },
  }
</script>

<style>
  .users-container {
    margin-bottom: 25%;
  }
  .users-container .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .users-container .users-icon {
    margin-right: 10px;
  }
  .users-container .users-action-bar {
    font-size: 15px;
  }
  #users .theme--light.v-btn.v-btn--disabled {
    color: rgba(0, 0, 0, 1) !important;
  }
  .v-select .v-select__slot .v-select__selections input[readonly='readonly'] {
    display: none;
  }
  .users-container .v-input {
    font-size: 0.875rem;
  }
  .users-container .user-name-edit-field .v-input.v-input--is-focused {
    min-width: 150px;
  }
  .users-container .activate-switch .v-input__control {
    height: 24px;
  }
  .theme--light tr.super-admin {
    background: #f3f3f3;
  }
  .theme--dark tr.super-admin {
    background: rgb(243, 243, 243, 0.17);
  }
  .activate-switch .theme--dark.v-icon {
    color: rgb(243, 243, 243, 0.2);
  }
  body
    .theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr.super-admin:hover:not(.v-data-table__expanded__content):not(
      .v-data-table__empty-wrapper
    ) {
    background: #ececec;
  }
  .users-container .users-email {
    display: flex;
    align-items: end;
  }
  .users-container .users-email.active {
    flex-direction: column;
    align-items: initial;
  }
  .users-container .users-email.active .possible-leader-select .v-input {
    margin: 0;
    padding: 0;
  }
  body svg.email-icon {
    width: 22px;
  }
  .email-icon path {
    fill: #a5d867;
  }
</style>
